import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  segmentedItemClass,
  segmentedListClass,
} from '@lawbee/ui';
import { CalendarDays, CalendarX, Check, CircleCheck, MoreHorizontal, Settings, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api, qs } from '../../../lib/api';
import { formatInTz, useApiMutation } from '../../../lib/hooks';
import { can, useMe } from '../../../lib/me';
import type { Booking, SettingsResponse } from '../../../lib/types';
import { BookingStatusBadge, CopyButton } from './shared';

type Range = 'upcoming' | 'week' | 'past';

const RANGES: { key: Range; label: string }[] = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'week', label: 'This week' },
  { key: 'past', label: 'Past 30 days' },
];

const DAY = 86_400_000;

function rangeBounds(range: Range): { from: string; to: string } {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === 'past') return { from: new Date(startOfToday.getTime() - 30 * DAY).toISOString(), to: now.toISOString() };
  if (range === 'week') return { from: startOfToday.toISOString(), to: new Date(startOfToday.getTime() + 7 * DAY).toISOString() };
  return { from: startOfToday.toISOString(), to: new Date(startOfToday.getTime() + 31 * DAY).toISOString() };
}

type BookingWithKind = Booking & { kind?: string };

export function BookingsPage() {
  const me = useMe();
  const tz = me.workspace.timezone;
  const [range, setRange] = useState<Range>('upcoming');
  const bounds = useMemo(() => rangeBounds(range), [range]);
  const q = useQuery({
    queryKey: ['bookings', range],
    queryFn: () => api.get<{ bookings: BookingWithKind[] }>(`/api/booking/bookings${qs(bounds)}`),
  });
  const settings = useQuery({ queryKey: ['workspace-settings'], queryFn: () => api.get<SettingsResponse>('/api/workspace/settings') });

  const groups = useMemo(() => {
    const list = [...(q.data?.bookings ?? [])].sort((a, b) => (range === 'past' ? b.startsAt.localeCompare(a.startsAt) : a.startsAt.localeCompare(b.startsAt)));
    const out: { key: string; label: string; items: BookingWithKind[] }[] = [];
    for (const b of list) {
      const key = formatInTz(b.startsAt, tz, { year: 'numeric', month: '2-digit', day: '2-digit' });
      let g = out.find((x) => x.key === key);
      if (!g) {
        g = { key, label: formatInTz(b.startsAt, tz, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }), items: [] };
        out.push(g);
      }
      g.items.push(b);
    }
    return out;
  }, [q.data, range, tz]);

  return (
    <div>
      <PageHeader
        title="Bookings"
        description="Consultations and meetings booked through your public booking pages."
        actions={
          <>
            {settings.data ? <CopyButton value={settings.data.publicUrls.bookingBase} label="Copy booking link" copiedLabel="Booking link copied" /> : null}
            {can(me, 'settings') ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/settings/booking">
                  <Settings /> Booking settings
                </Link>
              </Button>
            ) : null}
          </>
        }
      />
      <nav aria-label="Date range" className={`${segmentedListClass} mb-6`}>
        {RANGES.map((r) => (
          <button key={r.key} type="button" className={segmentedItemClass(range === r.key)} aria-pressed={range === r.key} onClick={() => setRange(r.key)}>
            {r.label}
          </button>
        ))}
      </nav>
      {q.isLoading ? <LoadingState label="Loading bookings" /> : null}
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : null}
      {q.data && groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={<CalendarDays />}
            title={range === 'past' ? 'No bookings in the last 30 days' : 'No bookings yet'}
            description="When clients book through your booking page, their appointments appear here."
            action={
              can(me, 'settings') ? (
                <Button asChild variant="outline">
                  <Link to="/settings/booking">Set up booking types</Link>
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : null}
      <div className="space-y-6">
        {groups.map((g) => (
          <section key={g.key} aria-label={g.label}>
            <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{g.label}</h2>
            <Card>
              <ul className="divide-y divide-border/70">
                {g.items.map((b) => (
                  <BookingRow key={b.id} booking={b} timezone={tz} canAct={can(me, 'client_work')} />
                ))}
              </ul>
            </Card>
          </section>
        ))}
      </div>
    </div>
  );
}

function BookingRow({ booking: b, timezone, canAct }: { booking: BookingWithKind; timezone: string; canAct: boolean }) {
  const update = useApiMutation((status: string) => api.patch(`/api/booking/bookings/${b.id}`, { status }), {
    success: 'Booking updated',
    invalidate: [['bookings'], ['dashboard']],
  });
  const closed = b.status === 'cancelled';
  return (
    <li className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:px-5">
      <div className="w-28 shrink-0">
        <p className="figures text-sm font-semibold">{formatInTz(b.startsAt, timezone, { hour: 'numeric', minute: '2-digit' })}</p>
        <p className="figures text-xs text-muted-foreground">to {formatInTz(b.endsAt, timezone, { hour: 'numeric', minute: '2-digit' })}</p>
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{b.name}</span>
          <BookingStatusBadge status={b.status} />
        </div>
        <p className="text-[13px] text-muted-foreground">{b.typeName}</p>
        <p className="flex flex-wrap gap-x-3 text-[13px]">
          <a href={`mailto:${b.email}`} className="truncate text-accent-foreground hover:underline">
            {b.email}
          </a>
          {b.phone ? (
            <a href={`tel:${b.phone}`} className="figures text-muted-foreground hover:underline">
              {b.phone}
            </a>
          ) : null}
        </p>
        {b.notes ? <p className="whitespace-pre-wrap text-[13px] text-muted-foreground">{b.notes}</p> : null}
      </div>
      {canAct ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="self-end sm:self-start" aria-label={`Actions for ${b.name}`} disabled={update.isPending}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {b.status !== 'confirmed' ? (
              <DropdownMenuItem icon={<Check />} onSelect={() => update.mutate('confirmed')}>
                Confirm
              </DropdownMenuItem>
            ) : null}
            {b.status !== 'completed' && !closed ? (
              <DropdownMenuItem icon={<CircleCheck />} onSelect={() => update.mutate('completed')}>
                Mark completed
              </DropdownMenuItem>
            ) : null}
            {b.status !== 'no_show' && !closed ? (
              <DropdownMenuItem icon={<UserX />} onSelect={() => update.mutate('no_show')}>
                No-show
              </DropdownMenuItem>
            ) : null}
            {!closed ? (
              <DropdownMenuItem icon={<CalendarX />} destructive onSelect={() => update.mutate('cancelled')}>
                Cancel booking
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
}
